/**
 * 鱼塘租赁 API（RESTful，对接渔管理后端）
 * 鉴权复用 src/service/api.js（Cookie + withCredentials）
 */
import api from "./api";
import axios from "axios";
import { Loading } from "element-ui";
import baseUrl from "./base-url";

const PROPERTY = "/api/FishPondLeaseProperty";
const TENANT = "/api/FishPondLeaseTenant";
const CONTRACT = "/api/FishPondLeaseContract";
const TEMPLATE = "/api/FishPondLeaseContractTemplate";
const RENT = "/api/FishPondLeaseRent";
const STAT = "/api/FishPondLeaseStat";
const CONFIG = "/api/FishPondLeaseConfig";
const HIERARCHY = "/api/FishPondLeaseCompanyHierarchy";
const AUDIT_LOG = "/api/FishPondLeaseAuditLog";

/**
 * 解析 /api/Resource 上传后的文件预览地址（文件存 Mongo GridFS，非 files CDN）
 * 完整形如：https://ylwfisheryapi.xxx/api/Resource/{name}
 */
export function resolveLeaseResourceUrl(pathOrName) {
  if (!pathOrName) return "";
  const s = String(pathOrName).trim();
  if (!s) return "";
  if (/^(https?:|blob:|data:)/i.test(s)) return s;
  const apiBase = (baseUrl.base || "").replace(/\/$/, "");
  const resourceIdx = s.indexOf("/api/Resource/");
  if (resourceIdx >= 0) {
    return apiBase + s.substring(resourceIdx);
  }
  if (/^api\/Resource\//i.test(s)) {
    return apiBase + "/" + s;
  }
  const name = s.replace(/^\/+/, "");
  const uploadBase = (baseUrl.imgUpload || apiBase + "/api/Resource").replace(
    /\/$/,
    ""
  );
  return `${uploadBase}/${name}`;
}

/** 从完整地址或文件名提取 Resource 文件名 */
export function getLeaseResourceName(pathOrName) {
  if (!pathOrName) return "";
  const s = String(pathOrName).trim();
  if (!s) return "";
  if (/^(blob:|data:)/i.test(s)) return "";
  const resourceIdx = s.indexOf("/api/Resource/");
  if (resourceIdx >= 0) {
    return s
      .substring(resourceIdx + "/api/Resource/".length)
      .split("?")[0]
      .replace(/^\/+/, "");
  }
  if (/^https?:\/\//i.test(s)) {
    const parts = s.split("/");
    return (parts[parts.length - 1] || "").split("?")[0];
  }
  return s.replace(/^\/+/, "").split("?")[0];
}

function guessResourceMime(name) {
  const ext = (String(name || "").split(".").pop() || "").toLowerCase();
  if (ext === "png") return "image/png";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "gif") return "image/gif";
  if (ext === "webp") return "image/webp";
  if (ext === "bmp") return "image/bmp";
  if (ext === "pdf") return "application/pdf";
  return "application/octet-stream";
}

function isLikelyJsonArrayBuffer(buf) {
  if (!buf || !buf.byteLength) return false;
  const bytes = new Uint8Array(buf.slice(0, 8));
  let i = 0;
  // skip UTF-8 BOM / whitespace
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) i = 3;
  while (i < bytes.length && (bytes[i] === 0x20 || bytes[i] === 0x0a || bytes[i] === 0x0d || bytes[i] === 0x09)) {
    i += 1;
  }
  return bytes[i] === 0x7b || bytes[i] === 0x5b; // { or [
}

/**
 * 带登录态拉取 Resource 图片并转为 blob URL（解决跨域 img 不带 Cookie 导致 401）
 */
export function loadLeaseResourceObjectUrl(pathOrName) {
  if (!pathOrName) return Promise.resolve("");
  const s = String(pathOrName).trim();
  if (!s) return Promise.resolve("");
  if (/^(blob:|data:)/i.test(s)) return Promise.resolve(s);

  const name = getLeaseResourceName(s);
  if (!name) return Promise.reject(new Error("empty resource name"));

  return axios({
    method: "GET",
    url: `/api/Resource/${name}`,
    baseURL: baseUrl.base,
    responseType: "arraybuffer",
    withCredentials: true
  }).then(res => {
    const buf = res && res.data;
    if (!buf || !buf.byteLength) {
      return Promise.reject(new Error("empty resource"));
    }
    // 鉴权失败时接口常返回 JSON，避免把错误响应当成图片
    const headerType = (res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) || "";
    if (/json|text|html/i.test(headerType) || isLikelyJsonArrayBuffer(buf)) {
      return Promise.reject(new Error("resource not image"));
    }
    const mime =
      (headerType && /^image\//i.test(headerType) && headerType.split(";")[0].trim()) ||
      guessResourceMime(name);
    return URL.createObjectURL(new Blob([buf], { type: mime }));
  });
}

function getErrorMessage(err) {
  const data = err && err.response && err.response.data;
  if (!data) return (err && err.message) || "请求失败";
  if (typeof data === "string") return data;
  return data.message || data.title || data.detail || "请求失败";
}

/** 统一错误提示（可选） */
export function leaseRequestError(err) {
  return getErrorMessage(err);
}

// ---------- 标的物 ----------
export const pageProperty = params => api.get(`${PROPERTY}/page`, { params });
export const getProperty = id => api.get(`${PROPERTY}/${id}`);
export const addProperty = data => api.post(PROPERTY, data);
export const editProperty = data => api.put(PROPERTY, data);
export const deleteProperty = id => api.delete(`${PROPERTY}/${id}`);
export const disableProperty = data => api.post(`${PROPERTY}/disable`, data);
export const submitPropertyAudit = data => api.post(`${PROPERTY}/submit`, data);
export const approveProperty = data => api.post(`${PROPERTY}/approve`, data);
export const rejectProperty = data => api.post(`${PROPERTY}/reject`, data);
export const pageAvailableProperty = params =>
  api.get(`${PROPERTY}/availableProperty/page`, { params });
export const listPropertyAttrDef = type =>
  api.get(`${PROPERTY}/attrDef/list`, { params: { type } });

// ---------- 承租方 ----------
export const pageTenant = params => api.get(`${TENANT}/page`, { params });
export const getTenant = id => api.get(`${TENANT}/${id}`);
export const checkTenantLoginPhone = phone =>
  api.get(`${TENANT}/CheckLoginPhone`, { params: { phone } });
export const addTenant = data => api.post(TENANT, data);
export const editTenant = data => api.put(TENANT, data);
export const deleteTenant = id => api.delete(`${TENANT}/${id}`);
export const submitTenantAudit = data => api.post(`${TENANT}/submit`, data);
export const approveTenant = data => api.post(`${TENANT}/approve`, data);
export const rejectTenant = data => api.post(`${TENANT}/reject`, data);
export const disableTenant = data => api.post(`${TENANT}/disable`, data);
export const exportTenant = params =>
  downloadBlobGet(`${TENANT}/export`, "承租方.xls", params || {});

// ---------- 合同 ----------
export const pageContract = params => api.get(`${CONTRACT}/page`, { params });
export const getContract = id => api.get(`${CONTRACT}/${id}`);
export const listContractByProperty = propertyId =>
  api.get(`${CONTRACT}/byProperty/${propertyId}`);
export const addContract = data => api.post(CONTRACT, data);
export const editContract = data => api.put(CONTRACT, data);
export const deleteContract = id => api.delete(`${CONTRACT}/${id}`);
export const signContract = data => api.post(`${CONTRACT}/sign`, data);
export const terminateContract = data => api.post(`${CONTRACT}/terminate`, data);
export const offlinePayContract = data =>
  api.post(`${CONTRACT}/offlinePay`, data);
export const pageContractAvailableProperty = params =>
  api.get(`${CONTRACT}/availableProperty/page`, { params });
export const downloadContractFile = id =>
  downloadBlobGet(`${CONTRACT}/file/download/${id}`, `合同_${id}.docx`);
export const downloadContractTemplate = assetType =>
  downloadBlobGet(
    `${CONTRACT}/template/download`,
    "合同空白模板.docx",
    { assetType }
  );

// ---------- 合同模板 ----------
export const pageTemplate = params => api.get(`${TEMPLATE}/page`, { params });
export const getTemplate = id => api.get(`${TEMPLATE}/${id}`);
export const addTemplate = data => api.post(TEMPLATE, data);
export const editTemplate = data => api.put(TEMPLATE, data);
export const deleteTemplate = id => api.delete(`${TEMPLATE}/${id}`);

// ---------- 租金 ----------
export const pageRent = params => api.get(`${RENT}/page`, { params });
export const addRent = data => api.post(RENT, data);
export const getRentStats = params => api.get(`${RENT}/stats`, { params });
export const exportRent = params =>
  downloadBlobGet(`${RENT}/export`, "租金记录.xls", params || {});
export const getRentVoucherUrl = id =>
  api.get(`${RENT}/voucher/download/${id}`);

// ---------- 统计 / 配置 / 层级 / 审核日志 ----------
export const getStatSummary = (params = {}) =>
  api.get(`${STAT}/summary`, { params });
export const listPropertyConfig = () => api.get(`${CONFIG}/list`);
export const updatePropertyConfig = data => api.put(CONFIG, data);
export const getHierarchyInfo = () => api.get(`${HIERARCHY}/info`);
export const getHierarchyChildren = () => api.get(`${HIERARCHY}/children`);
export const listAuditLog = params =>
  api.get(`${AUDIT_LOG}/list`, { params });

// ---------- 批量导入 ----------
export const downloadPropertyImportTemplate = () =>
  downloadBlobGet(
    `${PROPERTY}/import/template`,
    "标的物导入模板.xlsx"
  );
// 不要手动设 Content-Type，交给浏览器带 multipart boundary，否则后端 IFormFile 绑不上
export const importPropertyExcel = formData => api.post(`${PROPERTY}/import`, formData);
export const listLeaseFishBaseOptions = enterpriseId =>
  api.get(`${PROPERTY}/fishBase/options`, {
    params: enterpriseId ? { enterpriseId } : {}
  });
export const listLeaseFishPondOptions = (enterpriseId, fishBaseId) =>
  api.get(`${PROPERTY}/fishPond/options`, {
    params: {
      enterpriseId: enterpriseId || undefined,
      fishBaseId: fishBaseId || undefined
    }
  });
export const downloadTenantImportTemplate = () =>
  downloadBlobGet(`${TENANT}/import/template`, "承租方导入模板.xlsx");
export const importTenantExcel = formData => api.post(`${TENANT}/import`, formData);
export const pageTenantImport = params =>
  api.get(`${TENANT}/import/page`, { params });
export const deleteTenantImport = id => api.delete(`${TENANT}/import/${id}`);

// ---------- 辅助：塘口/基地下拉（渔管理现有接口） ----------
export const getFishBaseKeyValue = () => api.get("/api/FishBase/KeyValue");
export const getFishBasePage = (params = {}) =>
  api.get("/api/FishBase", {
    params: Object.assign({ pageIndex: 1, pageSize: 1000 }, params)
  });
export const getFishBaseDetail = id => api.get(`/api/FishBase/${id}`);
export const getPondListByBase = (baseId, fishBaseName) =>
  api.get(`/api/FishPond`, {
    params: {
      pageIndex: 1,
      pageSize: 1000,
      fishBaseName: fishBaseName || ""
    }
  });
export const getPondKeyValueByBase = baseId =>
  api.get(`/api/FishPond/KeyValue/${baseId}`);
export const getFishPondDetail = id => api.get(`/api/FishPond/${id}`);
export const getAllPondList = () => api.get("/api/FishPond/GetAllPondList");

function triggerDownload(blob, fileName) {
  const a = document.createElement("a");
  if ("download" in a) {
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  } else {
    navigator.msSaveBlob(blob, fileName);
  }
}

export function downloadBlobGet(url, fileName, params) {
  const loading = Loading.service({
    lock: true,
    text: "下载中...",
    spinner: "el-icon-loading",
    background: "rgba(0, 0, 0, 0.7)"
  });
  return axios({
    method: "GET",
    url,
    params,
    responseType: "blob",
    withCredentials: true
  })
    .then(res => {
      triggerDownload(new Blob([res.data]), fileName);
      return res;
    })
    .finally(() => loading.close());
}

export function downloadBlobPost(url, data, fileName) {
  const loading = Loading.service({
    lock: true,
    text: "导出中...",
    spinner: "el-icon-loading",
    background: "rgba(0, 0, 0, 0.7)"
  });
  return axios({
    method: "POST",
    url,
    data,
    responseType: "blob",
    withCredentials: true
  })
    .then(res => {
      triggerDownload(new Blob([res.data]), fileName);
      return res;
    })
    .finally(() => loading.close());
}
